/**
 * Themed confirmation dialog — replaces browser-native `window.confirm` for
 * destructive/irrevocable flows. Radix-backed, same Portal/Overlay pattern as
 * `Dialog`/`AddDeviceDialog` (see `Dialog.tsx`), but shaped for a
 * yes/no decision:
 *
 *   - Eyebrow-style title (mono, 11px, tracked 0.14em, uppercase).
 *   - Body copy in Inter Tight, muted body colour.
 *   - Left-edge poppy accent stripe when `destructive`.
 *   - Confirm button poppy-filled when `destructive`, navy otherwise.
 *   - Optional `typeToConfirm`: gates the confirm button until the user
 *     types the exact string. For the harshest flows (account reset, wipe).
 *
 * See `docs/debt/010-pwa-native-confirm-for-device-remove.md`.
 */

import * as R from '@radix-ui/react-dialog';
import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { cn } from '../lib/cn';
import { Button } from './Button';
import { Input } from './Input';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: ComponentChildren;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** When set, renders an Input; confirm stays disabled until the user types this exact string. */
  typeToConfirm?: string;
  onConfirm: () => Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  typeToConfirm,
  onConfirm,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const typeGateOk = typeToConfirm === undefined || typed === typeToConfirm;

  async function handleConfirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      setTyped('');
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  function handleOpenChange(next: boolean): void {
    if (busy) return;
    if (!next) {
      setTyped('');
      setError(null);
    }
    onOpenChange(next);
  }

  return (
    <R.Root open={open} onOpenChange={handleOpenChange}>
      <R.Portal>
        <R.Overlay class="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" />
        <R.Content
          class={cn(
            'fixed left-1/2 top-1/2 z-50 flex w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'overflow-hidden rounded-lg border border-line bg-surface shadow-2xl',
          )}
        >
          {destructive && (
            <div
              aria-hidden="true"
              class="w-1 shrink-0 self-stretch bg-signal"
            />
          )}
          <div class="flex-1 p-6">
            <R.Title
              class={cn(
                'font-mono text-[11px] tracking-[0.14em] uppercase',
                destructive ? 'text-signal' : 'text-surface-fg',
              )}
            >
              {title}
            </R.Title>
            <R.Description asChild>
              <div class="mt-3 text-sm leading-relaxed text-surface-body">
                {body}
              </div>
            </R.Description>

            {typeToConfirm !== undefined && (
              <div class="mt-4 space-y-1.5">
                <label
                  htmlFor="confirm-type-input"
                  class="block font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted"
                >
                  Type <span class="text-surface-fg">{typeToConfirm}</span> to
                  confirm
                </label>
                <Input
                  id="confirm-type-input"
                  value={typed}
                  onInput={(e: Event) => {
                    setTyped((e.target as HTMLInputElement).value);
                  }}
                  autocomplete="off"
                />
              </div>
            )}

            {error !== null && (
              <p class="mt-3 text-[13px] text-signal" role="alert">
                {error}
              </p>
            )}

            <div class="mt-6 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  handleOpenChange(false);
                }}
                disabled={busy}
              >
                {cancelLabel}
              </Button>
              <Button
                variant={destructive ? 'danger' : 'primary'}
                onClick={() => {
                  void handleConfirm();
                }}
                disabled={busy || !typeGateOk}
              >
                {busy ? 'Working…' : confirmLabel}
              </Button>
            </div>
          </div>
        </R.Content>
      </R.Portal>
    </R.Root>
  );
}
